import { useReduxState } from 'hooks';
import { Badge, Link, MoreOptions, User } from 'components/common';
import { Icon } from 'components/common/icons';
import { FeedbackDelete } from '.';

import type { GetFeedbacksRes } from 'shared-types/responses';

interface Params {
  feedback: GetFeedbacksRes['feedbacks'][0];
  isAdmin: boolean;
  showCourse: boolean;
  showUnread: boolean;
  updateFeedbacks: () => Promise<void>;
}

const FeedbackItem = ({
  feedback,
  isAdmin,
  showCourse,
  showUnread,
  updateFeedbacks
}: Params) => {
  const { messages, theme } = useReduxState();

  const feedbackTypes = {
    'factual-error': messages.feedbackFactualError,
    general: messages.feedbackGeneral,
    other: messages.commonOther,
    'spelling-error': messages.feedbackSpellingError,
    'unclear-content': messages.feedbackUnclearContent
  };

  return (
    <div
      className={`feedback ${showUnread && feedback.isUnread ? 'unread' : ''}`}
    >
      <div className="row-sm">
        <div className="col-1of4">
          <div className="header">
            <div className="user">
              <User
                date={feedback.createdAt}
                enableProfileLink={true}
                anonymous={!feedback.user}
                user={feedback.user}
              />
            </div>
          </div>
        </div>
        <div className="col">
          <div className="content">
            {showCourse && feedback.courseTitle ? (
              <div aria-label={messages.commonCourse} className="course-title">
                {feedback.courseTitle}
              </div>
            ) : null}

            {feedback.selectedText ? (
              <div aria-label={messages.readerFeedbackSelectedText}>
                <div className="selected-text">
                  <div className="selected-text-content">
                    {feedback.selectedText}
                  </div>

                  {feedback.chapterSlug && feedback.blockId ? (
                    <Link
                      as={`/courses/${feedback.courseSlug}/${feedback.chapterSlug}#${feedback.blockId}`}
                      href="/courses/[...courseParams]"
                      linkColor="inherit"
                      style={{ fontWeight: 400 }}
                    >
                      <div className="selected-text-link">
                        <div className="selected-text-link-icon">
                          <Icon icon="book" />
                        </div>

                        {messages.feedbackShowInChapter}
                      </div>
                    </Link>
                  ) : null}

                  {feedback.chapterSlug && feedback.blockId && isAdmin ? (
                    <Link
                      as={`/admin/courses/${feedback.courseSlug}/edit/${feedback.chapterSlug}#${feedback.blockId}`}
                      href="/admin/courses/[courseSlug]/edit/[chapterSlug]"
                      linkColor="inherit"
                      style={{ fontWeight: 400 }}
                    >
                      <div className="selected-text-link">
                        <div className="selected-text-link-icon">
                          <Icon icon="pen" />
                        </div>

                        {messages.commonEdit}
                      </div>
                    </Link>
                  ) : null}
                </div>
              </div>
            ) : null}

            <p aria-label={messages.commonText} className="comment">
              {feedback.comment}
            </p>
          </div>
          <div className="badges">
            <div className="badge">
              <Badge
                aria-label={messages.readerFeedbackReasonTitle}
                icon="tag"
                text={
                  feedbackTypes.hasOwnProperty(feedback.type)
                    ? feedbackTypes[feedback.type]
                    : feedbackTypes.other
                }
              />
            </div>
          </div>
        </div>
        {isAdmin ? (
          <div className="col-min">
            <div className="dropdown">
              <MoreOptions>
                <FeedbackDelete
                  feedback={feedback}
                  updateFeedbacks={updateFeedbacks}
                />
              </MoreOptions>
            </div>
          </div>
        ) : null}
      </div>

      <style jsx>{`
        .feedback {
          background-color: white;
          padding: 15px;
          position: relative;
        }
        .feedback.unread {
          border-left: 5px solid ${theme.textColorLighter};
        }
        .header {
          align-items: center;
          display: flex;
          margin-bottom: 15px;
        }
        .user {
          flex-grow: 1;
          flex-shrink: 1;
        }
        .dropdown {
          margin-left: 15px;
        }
        .course-title {
          margin-bottom: 15px;
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
          font-weight: 600;
        }
        .selected-text {
          border-left: 5px solid ${theme.lightGrey};
          color: ${theme.textColorLighter};
          display: block;
          font-size: 1.5rem;
          margin-bottom: 10px;
          padding: 5px 8px;
          text-decoration: none;
        }
        .selected-text-link {
          color: ${theme.textColor};
          cursor: pointer;
          display: inline-block;
          font-size: 1.3rem;
          margin: 15px 15px 0 0;
          text-decoration: none;
        }
        .selected-text-link:focus,
        .selected-text-link:hover {
          text-decoration: underline;
        }
        .selected-text-link-icon {
          color: ${theme.textColorLighter};
          display: inline-block;
          font-size: 1rem;
          margin-right: 5px;
          position: relative;
          top: -1px;
        }
        .comment {
          color: ${theme.textColor};
          font-size: 1.6rem;
          font-weight: 400;
          margin: 5px 0 0;
        }
        .badges {
          margin-top: 15px;
        }
        .badge {
          display: inline-block;
          margin-right: 15px;
        }
      `}</style>
    </div>
  );
};

export default FeedbackItem;
